<template>
  <div>
    <div class="left-column">
      <div class="mb-3 text-center video">
        <HMFilePreview
          v-if="getMedia"
          :file="getMedia"
          :preload="isViewingComment ? 'none' : 'metadata'"
          :key="getMedia.id"
          class="d-inline-block"
          @vimeoPlayerReady="onVimeoPlayerReady"
          @vimeoPlayerLoaded="onVimeoPlayerLoaded"
          @vimeoPlayerDurationChanged="onVimeoPlayerDurationChanged"
          @vimeoPlayerSeeking="onVimeoPlayerSeeking"
          @vimeoPlayerSeeked="onVimeoPlayerSeeked"
          @vimeoPlayerTimeupdate="onVimeoPlayerTimeupdate"
          @vimeoPlayerPaused="onVimeoPlayerPaused"
          @vimeoPlayerEnded="onVimeoPlayerEnded"
          @videoPlayerReady="onVideoPlayerReady"
          @videoPlayerLoaded="onVideoPlayerLoaded"
          @videoPlayerDurationChanged="onVideoPlayerDurationChanged"
          @videoPlayerSeeking="onVideoPlayerSeeking"
          @videoPlayerSeeked="onVideoPlayerSeeked"
          @videoPlayerTimeupdate="onVideoPlayerTimeupdate"
          @videoPlayerPaused="onVideoPlayerPaused"
          @videoPlayerEnded="onVideoPlayerEnded"
        />
      </div>
      <StudioMediaDetailsSidebar
        :sourceTitle="sourceTitle"
        :source="source"
        :playlist="playlist"
        :media="media"
        :sortOrder="sortOrder"
        class="right-column d-block d-lg-none"
      ></StudioMediaDetailsSidebar>
      <div class="text-block p-3">
        <div
          class="text-right w-100 mt-3"
          v-if="alternativePlaybackEnabled && media.media.alternative"
        >
          <a
            href
            @click.prevent="alternative = !alternative"
            class="underline c-brand-color"
          >
            {{ $t('STUDIO.TEXT.PLAYBACK_USE_ALTERNATIVE_SOURCE') }}
          </a>
        </div>
        <button
          v-if="isLesson && isBtnDisabledOrLessonCompleted"
          class="btn d-sm-block d-md-none d-lg-none w-100 bg-brand-color c-white mt-3 mb-5"
          type="button"
          name="button"
          :disabled="action.disabled"
          @click="action.emit && $emit(action.emit)"
        >
          <font-awesome-icon class="mr-2" :icon="['fas', 'check']" />
          {{ action.label }}
        </button>
        <div
          class="d-flex flex-column flex-lg-row justify-content-between mt-4 mb-4"
        >
          <div>
            <h1 class="title">{{ media.title }}</h1>
          </div>
          <div v-if="isLesson">
            <div
              v-if="isButtonDisabled"
              v-tooltip="{
                content: lessonToolTip,
                delay: 100,
                placement: 'top',
                container: 'body',
              }"
            >
              <button
                class="btn bg-brand-color c-white ml-lg-2"
                type="button"
                name="button"
                :disabled="action.disabled"
                @click="action.emit && $emit(action.emit)"
              >
                <font-awesome-icon class="mr-2" :icon="['fas', 'check']" />
                {{ action.label }}
              </button>
            </div>
            <div v-else>
              <button
                class="btn bg-brand-color c-white ml-lg-2"
                type="button"
                name="button"
                @click="action.emit && $emit(action.emit)"
              >
                <font-awesome-icon class="mr-2" :icon="['fas', 'check']" />
                {{ action.label }}
              </button>
            </div>
          </div>
        </div>
        <a
          v-if="getMedia.file_type === 'pdf'"
          :href="getMedia.storage_url"
          class="c-brand-color"
          target="_blank"
        >
          <small>{{ $t('STUDIO.LINKS.DOWNLOAD_PDF') }}</small>
        </a>
        <div
          class="d-flex flex-wrap justify-content-between align-items-center pb-3"
          :class="{
            'hm-bottom-border':
              (media.options && media.options.length) ||
              media.description ||
              media.description_new,
          }"
        >
          <div class="d-flex mt-2 smaller align-items-center">
            <HMTag
              :title="$t(getMediaTypeLabel(getMedia.file_content))"
              class="bg-charcoal c-white mr-3"
            />
            <HMInstructorsStack
              v-if="media.instructor"
              :instructors="[media.instructor]"
              size="32px"
            />
          </div>
          <div
            @click="toggleFavorite"
            class="d-flex cursor-pointer align-items-center mt-2"
          >
            <font-awesome-icon
              :icon="isFavourite ? ['fas', 'heart'] : ['far', 'heart']"
              class="heart mr-2"
            />
            <span class="d-none d-sm-block">{{
              isFavourite
                ? $t('STUDIO.LABELS.REMOVE_FROM_FAVORITES')
                : $t('STUDIO.LABELS.ADD_TO_FAVORITES')
            }}</span>
          </div>
        </div>
        <div
          v-if="media.options && media.options.length"
          class="d-flex align-items-center py-2 hm-bottom-border"
        >
          <HMTag
            class="smallest mr-1 my-1"
            :title="tag.option_name"
            v-for="tag in media.options"
            :key="tag.id"
          />
        </div>
        <HMCustomContent
          class="py-5"
          :html="media.description"
          :json="media.description_new"
        />
        <hr />
        <CommunityComments
          v-if="isLoggedIn && productCommentsEnabled"
          entityType="media"
          :entityId="media.id"
          :additionalData="{
            product_id: this.$route.params.product_id,
            lesson_id: this.$route.params.lesson_id,
            playlist_id: this.$route.params.playlist_id,
            title: media.title,
            studio_url: studioURL,
          }"
          :key="$route.params.media_id"
        />
      </div>
    </div>
    <StudioMediaDetailsSidebar
      :sourceTitle="sourceTitle"
      :source="source"
      :playlist="playlist"
      :media="media"
      :sortOrder="sortOrder"
      class="d-none d-lg-block right-column"
    ></StudioMediaDetailsSidebar>
  </div>
</template>

<script>
import Vue from 'vue';
import { getMediaTypeLabel } from '@/core/utils/media';
import HMTag from '@/web/components/common/HMTag';
import HMInstructorsStack from '@/web/components/common/HMInstructorsStack.vue';
import { getStudioURL, getRelativeUrl } from '@/web/router/custom-domain-utils';
import CommunityComments from '@/web/components/community/CommunityComments';
import StudioMediaDetailsSidebar from '@/web/components/products/StudioMediaDetailsSidebar';
import { mapGetters } from 'vuex';
import RightBlockSourceType from '@/core/typings/enums/RightBlockSourceType';
import VideoProgressTrackingMixin from '../../mixins/VideoProgressTrackingMixin';

export default Vue.extend({
  name: 'StudioPlaylistMediaDetails',
  mixins: [VideoProgressTrackingMixin],
  components: {
    HMTag,
    HMInstructorsStack,
    CommunityComments,
    StudioMediaDetailsSidebar,
  },
  props: {
    sourceTitle: {
      type: String,
    },
    source: {
      type: String,
    },
    media: {
      type: Object,
      required: true,
    },
    playlist: {
      type: Object,
    },
    sortOrder: {
      type: String,
    },
    activeMediaIndex: {
      type: Number,
    },
    isCompleteButtonDisabled: {
      type: Boolean,
      default: false,
    },
    lessonToolTip: {
      type: String,
    },
  },
  computed: {
    ...mapGetters({
      alternativePlaybackEnabled: 'studio/alternativePlaybackEnabled',
      productCommentsEnabled: 'product/productCommentsEnabled',
      isLoggedIn: 'user/isLoggedIn',
    }),
    getMedia() {
      return this.alternative ? this.media.media.alternative : this.media.media;
    },
    isMediaLocked() {
      return this.getMedia?.locked;
    },
    isButtonDisabled() {
      return (
        this.isCompleteButtonDisabled &&
        this.activeMediaIndex === this.playlist?.content?.length - 1
      );
    },
    isViewingComment() {
      return this.$route.query.comment;
    },
    studioURL() {
      return getStudioURL(this.$route);
    },
    isLesson() {
      return this.source === RightBlockSourceType.LESSON;
    },
    lessonPreventForwardSeeking() {
      if (!this.isLesson) {
        return false;
      }
      return this.playlist.prevent_forward_seeking;
    },
    isCompleted() {
      return this.playlist.completed;
    },
    isBtnDisabledOrLessonCompleted() {
      return this.isButtonDisabled || this.isCompleted;
    },
    action() {
      if (this.isButtonDisabled && !this.isCompleted) {
        return {
          emit: 'completeLesson',
          disabled: false,
          label: this.$t('STUDIO.BUTTON.COMPLETE'),
        };
      } else if (this.isButtonDisabled && this.isCompleted) {
        return {
          emit: null,
          disabled: true,
          label: this.$t('STUDIO.BUTTON.COMPLETED'),
        };
      } else {
        return {
          emit: 'completeAndContinue',
          disabled: false,
          label: this.$t('STUDIO.BUTTON.COMPLETE_AND_CONTINUE'),
        };
      }
    },
    mediaId() {
      return this.$route?.params?.media_id || this.media?.id;
    },
    mediaProgress() {
      return this.media?.progress;
    },
    mediaMinStart() {
      return this.getMedia?.playback_start;
    },
    preventForwardSeeking() {
      return (
        this.media?.prevent_forward_seeking || this.lessonPreventForwardSeeking
      );
    },
  },
  data() {
    return {
      isFavourite: false,
      alternative: false,
    };
  },
  watch: {
    media: {
      handler() {
        this.trackProductMediaView();
      },
      immediate: true,
    },
    $route: {
      handler(current, prev) {
        if (current.params.media_id !== prev.params.media_id) {
          this.$emit('onPlaylistMediaRouteChanged');
        }
      },
    },
  },
  methods: {
    completeLesson() {
      this.$emit('completeLesson');
    },
    completeAndContinue() {
      this.$emit('completeAndContinue');
    },
    getMediaTypeLabel,
    async toggleFavorite() {
      if (this.inProgress) {
        return;
      }
      this.inProgress = true;
      try {
        const returnedIsFavoriteState = await this.$store.dispatch(
          'product/likeMedia',
          {
            studioURL: getStudioURL(this.$route),
            productId: this.$route.params.product_id,
            mediaId: this.$route.params.media_id,
          }
        );
        this.isFavourite =
          String(returnedIsFavoriteState).toLowerCase() == 'true'; // transform returned string to boolean
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('STUDIO.NOTIFICATIONS.TOGGLE_FAVORITES_SUCCESS'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('STUDIO.NOTIFICATIONS.TOGGLE_FAVORITES_ERROR'),
          text: `Error: ${error}`,
          duration: -1, // stay ON
        });
      } finally {
        this.inProgress = false;
      }
    },
    trackProductMediaView() {
      // don't track locked media views
      if (this.isMediaLocked) return;

      const relativeUrl = this.$route.path;
      try {
        this.$store.dispatch('studio/trackProductMediaView', {
          studioURL: getStudioURL(this.$route),
          productId: this.$route.params.product_id,
          mediaId: this.$route.params.media_id,
          relativeUrl,
        });

        this.isFavourite = this.media.favorited;
      } catch (e) {
        console.log('Error tracking product media view');
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
.heart {
  color: var(--brand-color, $coral);
}
.hm-bottom-border {
  border-bottom: 1px solid $medium-gray;
}
.cursor-pointer {
  cursor: pointer;
}
.underline {
  text-decoration: underline;
}

.video {
  @include media-breakpoint-down(md) {
    margin-left: -15px;
    margin-right: -15px;
  }
}

.text-block {
  padding-right: 2em;

  @include media-breakpoint-down(md) {
    width: 100%;
    padding-right: 0;
    padding-left: 0;
  }
}

.left-column {
  width: 65%;
  @include media-breakpoint-down(md) {
    width: 100%;
  }
}

.right-column {
  width: 32%;
  @include media-breakpoint-down(md) {
    width: 100%;
  }
}

.title {
  flex: 1;
}
</style>
